import { VisitorData } from '@src/container/handleIframeMessage/types';
import { Targetings, FsTargetingOperator } from '../type';

/**
 * Handles array-based targeting values
 * For NOT operators: ALL values must not match (none should be present)
 * For other operators: AT LEAST ONE value must match
 */
function matchesArrayTargeting(targeting: Targetings, visitorData: VisitorData): boolean {
  if (!Array.isArray(targeting.value)) {
    return false;
  }

  const notOperator = [FsTargetingOperator.NOT_EQUALS, FsTargetingOperator.NOT_CONTAINS].includes(targeting.operator);

  return notOperator
    ? targeting.value.every((val) => matchesTargetingCriteria({ ...targeting, value: val }, visitorData))
    : targeting.value.some((val) => matchesTargetingCriteria({ ...targeting, value: val }, visitorData));
}

/**
 * Extracts the visitor's value for a given targeting key
 */
function getVisitorValue(targeting: Targetings, visitorData: VisitorData): unknown {
  return targeting.key === 'fs_users' ? visitorData.visitorId : visitorData.context[targeting.key];
}

/**
 * Evaluates a single targeting operator against a visitor value
 */
function evaluateOperator(operator: FsTargetingOperator, visitorValue: any, targetValue: any): boolean {
  const hasValue = visitorValue !== undefined && visitorValue !== null;

  switch (operator) {
    case FsTargetingOperator.EQUALS:
      return hasValue && visitorValue === targetValue;

    case FsTargetingOperator.NOT_EQUALS:
      return hasValue && visitorValue !== targetValue;

    case FsTargetingOperator.CONTAINS:
      return hasValue && visitorValue.toString().includes(targetValue.toString());

    case FsTargetingOperator.NOT_CONTAINS:
      return hasValue && !visitorValue.toString().includes(targetValue.toString());

    case FsTargetingOperator.EXISTS:
      return hasValue;

    case FsTargetingOperator.NOT_EXISTS:
      return !hasValue;

    case FsTargetingOperator.GREATER_THAN:
      return visitorValue > targetValue;

    case FsTargetingOperator.LOWER_THAN:
      return visitorValue < targetValue;

    case FsTargetingOperator.GREATER_THAN_OR_EQUALS:
      return visitorValue >= targetValue;

    case FsTargetingOperator.LOWER_THAN_OR_EQUALS:
      return visitorValue <= targetValue;

    case FsTargetingOperator.STARTS_WITH:
      return hasValue && visitorValue.toString().startsWith(targetValue.toString());

    case FsTargetingOperator.ENDS_WITH:
      return hasValue && visitorValue.toString().endsWith(targetValue.toString());

    default:
      return false;
  }
}

/**
 * Checks if a visitor matches the specified targeting criteria.
 *
 * @param targeting - The targeting rule to evaluate
 * @param visitorData - The visitor's data including context and ID
 * @returns true if the visitor matches the targeting criteria, false otherwise
 */
export function matchesTargetingCriteria(targeting: Targetings, visitorData?: VisitorData): boolean {
  if (targeting.key === 'fs_all_users' || !visitorData) {
    return true;
  }

  if (Array.isArray(targeting.value)) {
    return matchesArrayTargeting(targeting, visitorData);
  }

  const visitorValue = getVisitorValue(targeting, visitorData);

  return evaluateOperator(targeting.operator, visitorValue, targeting.value);
}
